import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  clearModalTask,
  getCalendarEvents,
  getTaskById,
} from 'store/modules/management_store/actions';
import { ApplicationState } from 'store';
import { ManagementState } from 'store/modules/management_store/types';
import moment from 'moment';
import Loader from 'components/atoms/loader';
import Checkbox from 'components/atoms/checkbox';
import companyId from 'services/company_id';
import { ProfileState } from 'store/modules/profile_store/types';
import tabId from 'services/tabId';
import { taskColor, taskDisplay } from 'services/task_appearance';
import TaskFilter from '../task_filter';
import ManagementTask from '../management_task';
import Calendar from '../calendar';

const WINDOW_DAYS_HALF = 60;

const CalendarPage = () => {
  const dispatch = useDispatch();
  const [filterLate, setFilterLate] = useState<boolean>(true);
  const [filterToday, setFilterToday] = useState<boolean>(true);
  const [filterFuture, setFilterFuture] = useState<boolean>(true);
  const [filterFinished, setFilterFinished] = useState<boolean>(true);
  const [filterBidding, setFilterBidding] = useState<boolean>(true);
  const [loadedRange, setLoadedRange] = useState<{ start: Date; end: Date } | null>(null);
  const [modalManagementTaskOpened, setModalManagementTaskOpened] = useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [pendingTaskId, setPendingTaskId] = useState<number | null>(null);

  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const inFlightRangeRef = useRef<{ start: Date; end: Date } | null>(null);

  const inFlightCancelRef = useRef<any>(null);

  const fetchWindow = (centerDate: Date) => {
    const start = moment(centerDate).subtract(WINDOW_DAYS_HALF, 'days').startOf('day');
    const end = moment(centerDate).add(WINDOW_DAYS_HALF, 'days').endOf('day');
    const range = { start: start.toDate(), end: end.toDate() };
    inFlightRangeRef.current = range;
    if (inFlightCancelRef.current) inFlightCancelRef.current.cancel('superseded');
    const source = axios.CancelToken.source();
    inFlightCancelRef.current = source;
    const result: any = dispatch(
      getCalendarEvents(
        management.filtered_by,
        companyId(profile),
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
        ['biddings', 'tasks', 'progress'],
        source.token,
      ),
    );

    const settle = (success: boolean) => {
      if (inFlightRangeRef.current === range) {
        inFlightRangeRef.current = null;
        inFlightCancelRef.current = null;
      }
      if (success) setLoadedRange(range);
    };
    if (result && typeof result.then === 'function') {
      result
        .then((response: any) => settle(!!response && response.status < 400))
        .catch(() => settle(false));
    } else {
      settle(true);
    }
  };

  const refetchCurrentWindow = () => {
    if (!loadedRange) return;
    if (inFlightCancelRef.current) inFlightCancelRef.current.cancel('superseded');
    const source = axios.CancelToken.source();
    inFlightCancelRef.current = source;
    dispatch(
      getCalendarEvents(
        management.filtered_by,
        companyId(profile),
        moment(loadedRange.start).format('YYYY-MM-DD'),
        moment(loadedRange.end).format('YYYY-MM-DD'),
        ['biddings', 'tasks', 'progress'],
        source.token,
      ),
    );
  };

  useEffect(
    () => () => {
      if (inFlightCancelRef.current) inFlightCancelRef.current.cancel('unmount');
    },
    [],
  );

  useEffect(() => {
    fetchWindow(new Date());
  }, [management.filtered_by, profile.currentCompany[tabId()]]);

  const handleDatesSet = (range: { start: Date; end: Date }) => {
    const covered = inFlightRangeRef.current || loadedRange;
    if (covered && range.start >= covered.start && range.end <= covered.end) return;
    const center = new Date((range.start.getTime() + range.end.getTime()) / 2);
    fetchWindow(center);
  };

  const handleTaskClick = (eventId: string) => {
    const taskId = Number(String(eventId).replace(/^t-/, ''));
    if (!taskId) return;
    setPendingTaskId(taskId);
    dispatch(getTaskById(taskId, companyId(profile)));
  };

  useEffect(() => {
    if (pendingTaskId == null) return;
    if (request.get_task_by_id?.loading) return;
    if (request.get_task_by_id?.loaded && management.modal_task?.id === pendingTaskId) {
      setSelectedTaskId(pendingTaskId);
      setModalManagementTaskOpened(true);
      setPendingTaskId(null);
    } else if (request.get_task_by_id?.error) {
      setPendingTaskId(null);
    }
  }, [
    request.get_task_by_id?.loading,
    request.get_task_by_id?.loaded,
    request.get_task_by_id?.error,
    management.modal_task,
    pendingTaskId,
  ]);

  const mutationCounterRef = useRef<number>(0);
  const mutationAtOpenRef = useRef<number>(0);
  const modalWasOpenRef = useRef<boolean>(false);

  const bump = () => {
    mutationCounterRef.current += 1;
  };
  useEffect(() => {
    if (request.update_tasks?.loaded) bump();
  }, [request.update_tasks?.loaded]);
  useEffect(() => {
    if (request.delete_task?.loaded) bump();
  }, [request.delete_task?.loaded]);
  useEffect(() => {
    if (request.create_tasks?.loaded) bump();
  }, [request.create_tasks?.loaded]);
  useEffect(() => {
    if (request.create_progress?.loaded) bump();
  }, [request.create_progress?.loaded]);
  useEffect(() => {
    if (request.update_progress?.loaded) bump();
  }, [request.update_progress?.loaded]);
  useEffect(() => {
    if (request.delete_progress?.loaded) bump();
  }, [request.delete_progress?.loaded]);

  useEffect(() => {
    if (modalManagementTaskOpened && !modalWasOpenRef.current) {
      mutationAtOpenRef.current = mutationCounterRef.current;
    }
    if (modalWasOpenRef.current && !modalManagementTaskOpened) {
      setSelectedTaskId(null);
      dispatch(clearModalTask());
      if (mutationCounterRef.current !== mutationAtOpenRef.current) {
        refetchCurrentWindow();
      }
    }
    modalWasOpenRef.current = modalManagementTaskOpened;
  }, [modalManagementTaskOpened]);

  const eventsList = useMemo(() => {
    const flags = { filterLate, filterToday, filterFuture, filterFinished };
    return (management.calendar_events || [])
      .filter((event) => {
        if (event.type === 'progress') return false;
        if (event.type === 'bidding' && !filterBidding) return false;
        return moment(event.date).isValid();
      })
      .map((event) => {
        if (event.type === 'task') {
          const finished = !!event.finished;
          return {
            id: event.id,
            title: event.title || '',
            start: moment(moment.parseZone(event.date).format('YYYY-MM-DDTHH:mm:ss')).toDate(),
            className: taskColor(event.date, finished),
            description: event.description || '',
            type: 'task',
            display: taskDisplay(event.date, finished, flags),
            bidding_id: event.bidding_id,
          };
        }
        return {
          id: event.id,
          title: event.title || '',
          start: moment(moment.parseZone(event.date).format('YYYY-MM-DDTHH:mm:ss')).toDate(),
          className: event.type === 'progress' ? 'bg-warning text-dark' : 'bg-light text-dark',
          description: event.description || '',
          type: event.type,
          bidding_id: event.bidding_id,
        };
      });
  }, [
    management.calendar_events,
    filterBidding,
    filterLate,
    filterToday,
    filterFuture,
    filterFinished,
  ]);

  const selectedTask =
    management.modal_task && management.modal_task.id === selectedTaskId
      ? management.modal_task
      : null;

  return (
    <div className="col-md-12">
      <div className="row mt-1 mb-2">
        <div className="col-md-1">
          <div className="mr-3">Exibir</div>
          <Checkbox
            label="Licitações"
            checked={filterBidding}
            disabled={false}
            handleChange={() => setFilterBidding(!filterBidding)}
          />
        </div>
        <div className="col-md-11">
          <div className="mr-3 mt-3 mt-md-0">Tarefas</div>
          <TaskFilter
            filterLate={filterLate}
            setFilterLate={setFilterLate}
            filterToday={filterToday}
            setFilterToday={setFilterToday}
            filterFuture={filterFuture}
            setFilterFuture={setFilterFuture}
            filterFinished={filterFinished}
            setFilterFinished={setFilterFinished}
          />
        </div>
      </div>
      {request.calendar_events?.loading && <Loader />}
      <Calendar eventsList={eventsList} onDatesSet={handleDatesSet} onTaskClick={handleTaskClick} />
      {modalManagementTaskOpened && selectedTask && (
        <ManagementTask
          modalManagementTaskOpened={modalManagementTaskOpened}
          setModalManagementTaskOpened={setModalManagementTaskOpened}
          bidding={selectedTask.bidding}
          taskType="task"
          task={selectedTask}
        />
      )}
    </div>
  );
};

export default CalendarPage;
